(() => {
  if (customElements.get('cahve-shop-the-edit')) return;

  class CahveShopTheEdit extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.section = this.closest('.cahve-shop-edit');
      this.products = Array.from(this.querySelectorAll('[data-cahve-product]'));
      this.hotspots = Array.from(this.querySelectorAll('[data-cahve-hotspot]'));
      this.addButton = this.querySelector('[data-cahve-add-all]');
      this.errorElement = this.querySelector('[data-cahve-error]');
      this.buttonLabel = this.querySelector('[data-cahve-button-label]');
      this.defaultButtonText = this.buttonLabel?.querySelector('.cahve-roll-label__text')?.textContent?.trim() || 'Add all to bag';

      this.bindInteractions();
      this.observeSection();
      this.syncButtonState();

      const firstProduct = this.products.find((product) => product.querySelector('[data-cahve-variant-select]')) || this.products[0];
      if (firstProduct) this.setActive(firstProduct.dataset.blockId, false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.revealObserver?.disconnect();
      window.clearTimeout(this.resetButtonTimer);
    }

    bindInteractions() {
      const options = { signal: this.signal };

      this.hotspots.forEach((hotspot) => {
        const blockId = hotspot.dataset.blockId;

        hotspot.addEventListener('mouseenter', () => this.setActive(blockId, true), options);
        hotspot.addEventListener('focus', () => this.setActive(blockId, true), options);
        hotspot.addEventListener('click', () => this.handleHotspotClick(blockId), options);
      });

      this.products.forEach((product) => {
        const blockId = product.dataset.blockId;
        const indexTrigger = product.querySelector('[data-cahve-product-trigger]');
        const variantSelect = product.querySelector('[data-cahve-variant-select]');

        product.addEventListener('mouseenter', () => this.setActive(blockId, true), options);
        product.addEventListener('focusin', () => this.setActive(blockId, true), options);
        indexTrigger?.addEventListener('click', () => this.setActive(blockId, true), options);
        variantSelect?.addEventListener('change', (event) => this.handleVariantChange(product, event.currentTarget), options);
      });

      this.addEventListener(
        'mouseleave',
        () => {
          if (!this.matches(':focus-within')) this.clearInteractionState();
        },
        options
      );

      this.addEventListener(
        'focusout',
        () => {
          window.requestAnimationFrame(() => {
            if (!this.matches(':hover, :focus-within')) this.clearInteractionState();
          });
        },
        options
      );

      this.addButton?.addEventListener('click', () => this.addAllToCart(), options);

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!this.contains(event.target)) return;
          const product = event.target.closest('[data-cahve-product]');
          if (!product) return;
          this.setActive(product.dataset.blockId, true);
        },
        options
      );
    }

    observeSection() {
      if (!this.section || this.section.dataset.cahveAnimate !== 'true') {
        this.section?.classList.add('is-visible');
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.section.classList.add('is-visible');
        return;
      }

      this.revealObserver?.disconnect();
      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
      );

      this.revealObserver.observe(this.section);
    }

    setActive(blockId, interacting = true) {
      if (!blockId) return;
      this.activeBlockId = blockId;
      this.classList.toggle('is-interacting', interacting);

      this.products.forEach((product) => {
        const isActive = product.dataset.blockId === blockId;
        product.classList.toggle('is-active', isActive);
        product.querySelector('[data-cahve-product-trigger]')?.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.hotspots.forEach((hotspot) => {
        const isActive = hotspot.dataset.blockId === blockId;
        hotspot.classList.toggle('is-active', isActive);
        hotspot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    clearInteractionState() {
      this.classList.remove('is-interacting');
    }

    handleHotspotClick(blockId) {
      this.setActive(blockId, true);
      const product = this.products.find((item) => item.dataset.blockId === blockId);
      if (!product || window.matchMedia('(min-width: 990px)').matches) return;

      product.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
    }

    handleVariantChange(product, select) {
      const option = select.selectedOptions[0];
      if (!option) return;

      const price = product.querySelector('[data-cahve-price]');
      const comparePrice = product.querySelector('[data-cahve-compare-price]');
      const image = product.querySelector('.cahve-shop-edit__product-image');

      if (price) price.textContent = option.dataset.price || '';

      if (comparePrice) {
        const onSale = option.dataset.onSale === 'true';
        comparePrice.textContent = onSale ? option.dataset.comparePrice || '' : '';
        comparePrice.hidden = !onSale;
      }

      if (image && option.dataset.imageUrl) {
        image.removeAttribute('srcset');
        image.src = option.dataset.imageUrl;
        image.alt = option.dataset.imageAlt || image.alt;
      }

      this.hideError();
      this.syncButtonState();
    }

    getExpectedItemCount() {
      return this.dataset.productLink || this.dataset.addMode === 'bundle_product'
        ? 1
        : Number.parseInt(this.dataset.productCount || '0', 10);
    }

    getItems() {
      if (this.dataset.addMode === 'bundle_product') {
        const id = Number.parseInt(this.dataset.bundleVariantId || '', 10);
        return id && this.dataset.bundleAvailable === 'true' ? [{ id, quantity: 1 }] : [];
      }

      return this.products
        .map((product) => {
          const select = product.querySelector('[data-cahve-variant-select]');
          const selectedOption = select?.selectedOptions?.[0];
          const id = Number.parseInt(select?.value || '', 10);
          const quantity = Number.parseInt(product.dataset.quantity || '1', 10);
          const available = selectedOption?.dataset.available === 'true';

          if (!id || !available) return null;
          return {
            id,
            quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
          };
        })
        .filter(Boolean);
    }

    syncButtonState() {
      if (!this.addButton) return;

      const expectedCount = this.getExpectedItemCount();
      const items = this.getItems();
      const ready = Boolean(this.dataset.productLink) || (expectedCount > 0 && items.length === expectedCount);

      if (!this.addButton.classList.contains('is-loading')) {
        this.addButton.disabled = !ready;
      }
    }

    async getLinkedProductItems() {
      const url = new URL(this.dataset.productLink, window.location.origin);
      const match = url.pathname.match(/^(.*\/products\/[^/]+)\/?$/);
      if (url.origin !== window.location.origin || !match) {
        throw new Error('Please select a product from this store in the section settings.');
      }

      const response = await fetch(match[1] + '.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('This bundle could not be loaded. Please try again.');
      const product = await response.json();
      const variantId = url.searchParams.get('variant');
      const variant = variantId
        ? product.variants?.find((item) => String(item.id) === variantId)
        : product.variants?.find((item) => item.available);
      if (!variant?.available) throw new Error('This bundle is currently unavailable.');
      return [{ id: variant.id, quantity: 1 }];
    }

    async addAllToCart() {
      if (!this.addButton || this.addButton.disabled) return;

      window.clearTimeout(this.resetButtonTimer);
      this.setLoading(true);
      this.hideError();

      try {
        const items = this.dataset.productLink
          ? await this.getLinkedProductItems()
          : this.getItems();
        if (!items.length || items.length !== this.getExpectedItemCount()) {
          throw new Error('Please choose an available option for every product.');
        }

        const cartSurface = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sectionIds = ['cart-icon-bubble'];

        cartSurface?.setActiveElement?.(document.activeElement);

        if (cartSurface?.getSectionsToRender) {
          cartSurface.getSectionsToRender().forEach((section) => {
            if (section?.id && !sectionIds.includes(section.id)) sectionIds.push(section.id);
          });
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });

        const state = await response.json();

        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'The products could not be added to your bag.');
        }

        if (cartSurface?.renderContents) {
          cartSurface.renderContents(state);
        } else {
          this.renderCartBubble(state.sections?.['cart-icon-bubble']);
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: `cahve-shop-the-edit-${this.dataset.sectionId}`,
            cartData: state,
          });
        }

        this.setButtonText('Added to bag');
        this.resetButtonTimer = window.setTimeout(() => this.setButtonText(this.defaultButtonText), 1800);
      } catch (error) {
        this.showError(error.message || 'Something went wrong. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    renderCartBubble(html) {
      if (!html) return;
      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(html, 'text/html');
      const nextBubble = documentFragment.querySelector('.shopify-section');
      const currentBubble = document.getElementById('shopify-section-cart-icon-bubble');

      if (nextBubble && currentBubble) currentBubble.innerHTML = nextBubble.innerHTML;
    }

    setLoading(isLoading) {
      if (!this.addButton) return;
      this.addButton.classList.toggle('is-loading', isLoading);
      this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.addButton.disabled = isLoading;

      if (!isLoading) this.syncButtonState();
    }

    setButtonText(text) {
      if (!this.buttonLabel) return;
      this.buttonLabel.querySelectorAll('.cahve-roll-label__text').forEach((label) => {
        label.textContent = text;
      });
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
      this.errorElement.focus?.();
    }

    hideError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }
  }

  customElements.define('cahve-shop-the-edit', CahveShopTheEdit);
})();
